import React, { useState, useEffect } from "react";
import {
  ChevronDown,
  Github,
  Linkedin,
  Mail,
  ArrowRight,
  ExternalLink,
  Send,
} from "lucide-react";
import "./portifolio.css";

const Portfolio = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Create mailto link with form data
      const mailtoLink = `mailto:dharmatejaneralla287@gmail.com?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(
        `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`,
      )}`;

      window.location.href = mailtoLink;

      setSubmitStatus({
        type: "success",
        message:
          "Opening your email client... Please send the email to complete your message.",
      });

      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          name: "",
          email: "",
          subject: "",
          message: "",
        });
        setSubmitStatus(null);
      }, 3000);
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message: "Something went wrong. Please try again or email me directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const projects = [
    {
      title: "Autonomous Navigation Robot",
      category: "Robotics",
      description:
        "Developed an intelligent robot with real-time obstacle detection and path planning using ROS and computer vision",
      tech: ["ROS", "Python", "OpenCV", "Arduino"],
      emoji: "🤖",
      gradient: "linear-gradient(135deg, #fce4ec 0%, #e1bee7 100%)",
    },
    {
      title: "E-Commerce Platform",
      category: "Web Development",
      description:
        "Full-stack application with payment integration, real-time inventory, and advanced analytics dashboard",
      tech: ["React", "Node.js", "MongoDB", "Stripe"],
      emoji: "💻",
      gradient: "linear-gradient(135deg, #bbdefb 0%, #b2ebf2 100%)",
    },
    {
      title: "IoT Monitoring System",
      category: "Full-Stack + Robotics",
      description:
        "Real-time dashboard for managing robotic fleet with sensor data visualization and remote control",
      tech: ["React", "WebSocket", "Raspberry Pi", "MQTT"],
      emoji: "🔗",
      gradient: "linear-gradient(135deg, #c8e6c9 0%, #b2dfdb 100%)",
    },
    {
      title: "AI-Powered Assistant",
      category: "AI/ML",
      description:
        "Natural language processing chatbot with context awareness and multi-turn conversation support",
      tech: ["Python", "TensorFlow", "FastAPI", "React"],
      emoji: "🧠",
      gradient: "linear-gradient(135deg, #ffe0b2 0%, #ffccbc 100%)",
    },
    {
      title: "Gesture Control System",
      category: "Robotics",
      description:
        "Computer vision based hand gesture recognition for controlling robotic arm movements wirelessly",
      tech: ["Python", "MediaPipe", "ROS", "C++"],
      emoji: "🤖",
      gradient: "linear-gradient(135deg, #e1bee7 0%, #f8bbd0 100%)",
    },
    {
      title: "Courier and Logistics Management System (FDC)",
      category: "Web Development",
      description:
        "Collaborative project management tool with real-time updates and team productivity analytics",
      tech: ["React", "Firebase", "TypeScript"],
      emoji: "💻",
      gradient: "linear-gradient(135deg, #f8bbd0 0%, #fce4ec 100%)",
    },
  ];

  const skills = {
    robotics: {
      title: "Robotics & Automation",
      icon: "🤖",
      items: [
        "ROS/ROS2",
        "Computer Vision",
        "Sensor Fusion",
        "Path Planning",
        "Arduino",
        "Raspberry Pi",
        "SLAM",
        "Gazebo",
      ],
    },
    development: {
      title: "Web Development",
      icon: "💻",
      items: [
        "React",
        "Node.js",
        "Python",
        "TypeScript",
        "MongoDB",
        "PostgreSQL",
        "REST APIs",
        "GraphQL",
      ],
    },
    ai: {
      title: "AI & Machine Learning",
      icon: "🧠",
      items: [
        "TensorFlow",
        "PyTorch",
        "OpenCV",
        "Scikit-learn",
        "Deep Learning",
        "NLP",
        "Data Analysis",
        "Neural Networks",
      ],
    },
    tools: {
      title: "Tools & Technologies",
      icon: "⚡",
      items: [
        "Git",
        "Docker",
        "Linux",
        "AWS",
        "Firebase",
        "MATLAB",
        "Fusion 360",
        "VS Code",
      ],
    },
  };

  const services = [
    {
      icon: "🤖",
      title: "Robotics Development",
      description:
        "Custom autonomous systems, robot navigation, sensor integration, and control algorithms",
    },
    {
      icon: "🌐",
      title: "Web Applications",
      description:
        "Full-stack development with modern frameworks, responsive design, and cloud deployment",
    },
    {
      icon: "🔗",
      title: "IoT Solutions",
      description:
        "Connected devices, real-time monitoring systems, and hardware-software integration",
    },
    {
      icon: "🎨",
      title: "UI/UX Design",
      description:
        "Beautiful, intuitive interfaces with focus on user experience and accessibility",
    },
  ];

  return (
    <div className="portfolio">
      {/* Animated Background */}
      <div className="background-blobs">
        <div className="blob blob-1"></div>
        <div className="blob blob-2"></div>
        <div className="blob blob-3"></div>
      </div>

      {/* Navigation */}
      <nav className={`navbar ${isScrolled ? "navbar-scrolled" : ""}`}>
        <div className="container navbar-content">
          <div className="logo">
            <span className="gradient-text">Dharma Teja</span>
          </div>
          <div className="nav-links">
            <a href="#about">About</a>
            <a href="#projects">Projects</a>
            <a href="#skills">Skills</a>
            <a href="#contact">Contact</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="hero-section">
        <div className="container hero-content">
          <div className="availability-badge fade-in delay-1">
            <span className="status-dot"></span>
            Available for opportunities
          </div>

          <h1 className="hero-title fade-in delay-2">
            Hi, I'm <span className="gradient-text">Dharma Teja</span>
            <span className="wave">👋</span>
          </h1>

          <div className="hero-subtitle fade-in delay-3">
            Robotics Engineer & Full Stack Developer
          </div>

          <p className="hero-description fade-in delay-4">
            I create intelligent systems and beautiful web experiences.
            Passionate about robotics, AI, and building technology that makes a
            difference.
          </p>

          <div className="hero-buttons fade-in delay-5">
            <a href="#projects" className="btn btn-primary">
              View My Work
              <ArrowRight size={20} />
            </a>
            <button className="btn btn-secondary">Download Resume</button>
          </div>

          <div className="social-links fade-in delay-6">
            <a
              href="https://github.com/dharmatejan-dev"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon"
            >
              <Github size={24} />
            </a>
            <a
              href="https://linkedin.com"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon"
            >
              <Linkedin size={24} />
            </a>
            <a
              href="mailto:dharmatejaneralla287@gmail.com"
              className="social-icon"
            >
              <Mail size={24} />
            </a>
          </div>
        </div>

        <div className="scroll-indicator">
          <ChevronDown size={32} />
        </div>

        {/* Decorative Elements */}
        <div className="decorative-shape shape-1"></div>
        <div className="decorative-shape shape-2"></div>
      </section>

      {/* About Section */}
      <section id="about" className="section">
        <div className="container">
          <h2 className="section-title">
            <span className="gradient-text">About Me</span>
          </h2>

          <div className="about-grid">
            <div className="about-image-card">
              <div className="about-emoji">🤖</div>
              <div className="about-stats">
                <div className="stat">
                  <div className="stat-number gradient-text">10+</div>
                  <div className="stat-label">Projects</div>
                </div>
                <div className="stat">
                  <div className="stat-number gradient-text">3+</div>
                  <div className="stat-label">Years</div>
                </div>
                <div className="stat">
                  <div className="stat-number gradient-text">15+</div>
                  <div className="stat-label">Technologies</div>
                </div>
              </div>
            </div>

            <div className="about-content">
              <p className="about-text">
                I'm a technology enthusiast who bridges the gap between hardware
                and software. My journey started with web development and
                naturally evolved into robotics, where I discovered my passion
                for creating intelligent, autonomous systems.
              </p>
              <p className="about-text">
                Whether it's programming a robot to navigate autonomously or
                building a full-stack web application, I love tackling complex
                challenges and turning ideas into reality.
              </p>
              <p className="about-text">
                When I'm not coding or tinkering with robots, you'll find me
                exploring new technologies, contributing to open-source
                projects, or sharing knowledge with the developer community.
              </p>
              <div className="tags">
                {[
                  "Robotics",
                  "Web Dev",
                  "AI/ML",
                  "IoT",
                  "Cloud",
                  "Open Source",
                ].map((tag) => (
                  <span key={tag} className="tag">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section section-alt">
        <div className="container">
          <h2 className="section-title">
            <span className="gradient-text">What I Do</span>
          </h2>

          <div className="services-grid">
            {services.map((service, idx) => (
              <div key={idx} className="service-card">
                <div className="service-icon">{service.icon}</div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="section">
        <div className="container">
          <h2 className="section-title">
            <span className="gradient-text">Featured Projects</span>
          </h2>

          <div className="projects-grid">
            {projects.map((project, idx) => (
              <div key={idx} className="project-card">
                <div
                  className="project-header"
                  style={{ background: project.gradient }}
                >
                  <div className="project-emoji">{project.emoji}</div>
                </div>

                <div className="project-content">
                  <div className="project-category">{project.category}</div>
                  <h3 className="project-title">{project.title}</h3>
                  <p className="project-description">{project.description}</p>

                  <div className="project-tech">
                    {project.tech.map((tech, i) => (
                      <span key={i} className="tech-tag">
                        {tech}
                      </span>
                    ))}
                  </div>

                  <button className="project-btn">
                    View Project
                    <ExternalLink size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="section section-alt">
        <div className="container">
          <h2 className="section-title">
            <span className="gradient-text">Skills & Technologies</span>
          </h2>

          <div className="skills-grid">
            {Object.entries(skills).map(([key, category]) => (
              <div key={key} className="skill-card">
                <div className="skill-header">
                  <span className="skill-icon">{category.icon}</span>
                  <h3 className="skill-title">{category.title}</h3>
                </div>
                <div className="skill-items">
                  {category.items.map((skill, i) => (
                    <div key={i} className="skill-item">
                      <span className="skill-dot"></span>
                      <span className="skill-name">{skill}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section">
        <div className="container">
          <h2 className="section-title">
            <span className="gradient-text">Let's Work Together</span>
          </h2>

          <p className="contact-intro">
            Have a project in mind or want to collaborate? I'd love to hear from
            you!
          </p>

          <div className="contact-form-wrapper">
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Enter your name"
                  className="form-input"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Enter your email"
                  className="form-input"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="subject" className="form-label">
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  placeholder="What's this about?"
                  className="form-input"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  placeholder="Tell me more about your project..."
                  rows="6"
                  className="form-textarea"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-full"
                disabled={isSubmitting}
              >
                <Send size={20} />
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>

              {submitStatus && (
                <div className={`submit-message ${submitStatus.type}`}>
                  {submitStatus.message}
                </div>
              )}

              <div className="contact-divider">
                <span>Or connect with me on</span>
              </div>

              <div className="contact-social">
                <a
                  href="https://github.com/dharmatejan-dev"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-social-btn btn-github"
                >
                  <Github size={20} />
                  GitHub
                </a>
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-social-btn btn-linkedin"
                >
                  <Linkedin size={20} />
                  LinkedIn
                </a>
                <a
                  href="mailto:dharmatejaneralla287@gmail.com"
                  className="contact-social-btn btn-email"
                >
                  <Mail size={20} />
                  Email
                </a>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container footer-content">
          <div className="footer-logo gradient-text">Dharma Teja N</div>
          <p className="footer-subtitle">
            Robotics Engineer × Full Stack Developer
          </p>
          <div className="footer-tagline">
            <span className="heart">❤️</span>
            <span>Building the future, one project at a time</span>
          </div>
          <p className="footer-copyright">
            © 2026 Dharma Teja. Designed with care and creativity
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Portfolio;
